import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare, Zap, AlertCircle, TrendingUp } from "lucide-react";

export const UsageMonitor = () => {
  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*, subscription_plans(*)')
        .eq('organization_id', user.id)
        .eq('status', 'active')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['message-usage', subscription?.id],
    queryFn: async () => {
      if (!subscription?.id) return null;

      const { data, error } = await supabase
        .from('message_usage')
        .select('*')
        .eq('subscription_id', subscription.id)
        .lte('period_start', new Date().toISOString())
        .gte('period_end', new Date().toISOString())
        .single();
      
      if (error && error.code !== 'PGRST116') {
        // No usage record yet, return zero usage
        return {
          messages_sent: 0,
          messages_received: 0,
          ai_messages: 0,
          overage_messages: 0
        };
      }
      return data;
    },
    enabled: !!subscription?.id
  });

  const { data: addons } = useQuery({
    queryKey: ['subscription-addons', subscription?.id],
    queryFn: async () => {
      if (!subscription?.id) return [];

      const { data, error } = await supabase
        .from('subscription_addon_assignments')
        .select('*, subscription_addons(*)')
        .eq('subscription_id', subscription.id);
      
      if (error) throw error;
      return data;
    },
    enabled: !!subscription?.id
  });

  if (subLoading || usageLoading) {
    return <div className="text-center py-4">Loading usage data...</div>;
  }

  if (!subscription) {
    return (
      <Card className="border-warning">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-warning" />
            No Active Subscription
          </CardTitle>
          <CardDescription>
            You need an active subscription to use messaging features.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const totalMessages = (usage?.messages_sent || 0) + (usage?.messages_received || 0);
  const messageLimit = subscription.subscription_plans?.message_limit || 0;
  const rolloverCredits = subscription.rollover_credits || 0;
  const rolloverExpiration = subscription.rollover_expiration_date;
  const effectiveLimit = messageLimit + rolloverCredits;
  const usagePercentage = (totalMessages / effectiveLimit) * 100;
  const overageMessages = Math.max(0, totalMessages - effectiveLimit);
  const overageBundles = Math.ceil(overageMessages / 20);
  const overageCost = overageBundles * 1.50;

  const daysRemaining = Math.ceil(
    (new Date(subscription.current_period_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Plan: {subscription.subscription_plans?.name}</CardTitle>
              <CardDescription>
                Billing {subscription.billing_cycle} • {daysRemaining} days remaining in cycle
              </CardDescription>
            </div>
            <Badge variant={subscription.status === 'active' ? 'default' : 'destructive'}>
              {subscription.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Message Usage</span>
              <span className="text-muted-foreground">
                {totalMessages} / {effectiveLimit} messages
              </span>
            </div>
            {rolloverCredits > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Monthly: {messageLimit} + Rollover: {rolloverCredits}</span>
                {rolloverExpiration && (
                  <span>Rollover expires {new Date(rolloverExpiration).toLocaleDateString()}</span>
                )}
              </div>
            )}
            <Progress value={Math.min(usagePercentage, 100)} className="h-2" />
            {usagePercentage >= 80 && (
              <p className="text-sm text-warning flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                You've used {usagePercentage.toFixed(0)}% of your available credits
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MessageSquare className="h-4 w-4" />
                Sent
              </div>
              <div className="text-2xl font-bold">{usage?.messages_sent || 0}</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                Received
              </div>
              <div className="text-2xl font-bold">{usage?.messages_received || 0}</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Zap className="h-4 w-4" />
                AI Messages
              </div>
              <div className="text-2xl font-bold">{usage?.ai_messages || 0}</div>
            </div>
          </div>

          {overageMessages > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-warning">Overage Messages</div>
                  <div className="text-sm text-muted-foreground">
                    {overageMessages} messages = {overageBundles} bundle{overageBundles !== 1 ? 's' : ''} ({overageBundles * 20} messages)
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    $1.50 per 20-message bundle
                  </div>
                </div>
                <div className="text-2xl font-bold text-warning">
                  ${overageCost.toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {addons && addons.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Add-ons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {addons.map((assignment) => (
                <div key={assignment.id} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{assignment.subscription_addons?.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {assignment.subscription_addons?.description}
                    </div>
                  </div>
                  <div className="text-sm font-medium">
                    ${Number(assignment.subscription_addons?.monthly_price).toFixed(2)}/every 4 weeks
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
